import { UI } from '../nextpage';

export class Forms {
    constructor(private NP: UI) {
        /*
            All form elements are opt-in so we don't erroneously override your intended behavior.
            Add the class "branded" to any standard form element and it will be styled appropriately.
            Or add the class "branded" to a <form> itself and all form elements within will be styled.

            Certain elements may be hidden or transparent due to CSS styling limitations (e.g. <select>).
            These elements will have auto-generated HTML to replace them and simulate their functionality.
        */

        // Simulate a <select multiple> option click.
        this.NP.registerEvent("selectMultiOptionClick", (e: MouseEvent) => {
            const thisLi = e.target as HTMLLIElement;
            const ctrlPressed = e.ctrlKey;
            const shiftPressed = e.shiftKey;
            const isSelected = thisLi.classList.contains("aria-selected");

            const dropdownParent = thisLi.parentElement;
            const thisLiIndex = [].indexOf.call(dropdownParent.children, thisLi) as number;
            const connectedSelect = dropdownParent.nextElementSibling as HTMLSelectElement;

            // If the shift key is not being pressed when clicked skip this portion
            if (!shiftPressed) {
                const connectedOption = connectedSelect.options[thisLiIndex];
                // If the ctrl key is pressed only modify the one option and connected element
                if (ctrlPressed) {
                    this.NP.toggleAriaState(thisLi, "selected", !isSelected);
                    thisLi.dataset.lastClicked = 'true';
                    [].slice.call(dropdownParent.children).forEach((cn: HTMLLIElement) => {
                        const thisCn = cn;
                        if (thisCn === thisLi || thisCn.getAttribute('role') !== 'option') {
                            return;
                        }
                        thisCn.dataset.lastClicked = 'false';
                    });
                    connectedOption.selected = !isSelected;
                    connectedSelect.dispatchEvent(new Event('change'));
                    return;
                }
                // Otherwise set this option to selected and all others to not selected.
                this.NP.toggleAriaState(thisLi, "selected", true);
                thisLi.dataset.lastClicked = 'true';
                [].slice.call(dropdownParent.children).forEach((cn: HTMLLIElement) => {
                    const thisCn = cn;
                    if (thisCn === thisLi || thisCn.getAttribute('role') !== 'option') {
                        return;
                    }
                    this.NP.toggleAriaState(thisCn, "selected", false);
                    thisCn.dataset.lastClicked = 'false';
                });
                for (let i = 0; i < connectedSelect.options.length; i++) {
                    (connectedSelect.options[i]).selected = false;
                }
                connectedOption.selected = true;
                connectedSelect.dispatchEvent(new Event('change'));
                return;
            }

            // If the shift key is pressed we simulate the same as a standard <select multiple> element.
            let lastClickedOption: HTMLLIElement = null;
            let lastClickedIndex = -1;
            [].slice.call(dropdownParent.children).forEach((cn: HTMLLIElement) => {
                if (lastClickedOption === null) {
                    lastClickedIndex = lastClickedIndex + 1;
                }
                if (cn.getAttribute('role') === 'option' && cn.dataset.lastClicked === 'true') {
                    lastClickedOption = cn;
                }
            });

            // If the element being clicked was the last element clicked do nothing
            if (lastClickedOption !== null && lastClickedOption === thisLi) {
                return;
            }

            // If the last element clicked cannot be found or is this element do nothing
            if (lastClickedIndex === -1 || thisLiIndex === -1 || lastClickedIndex === thisLiIndex) {
                return;
            }

            // Find the range of options to be selected between the last clicked option and this one
            let startIndex: number;
            let endIndex: number;
            if (lastClickedIndex > thisLiIndex) {
                startIndex = thisLiIndex;
                endIndex = lastClickedIndex;
            } else {
                startIndex = lastClickedIndex;
                endIndex = thisLiIndex;
            }

            // Set all options not within the range to not be selected,
            // then set all options within the range to selected
            let i = -1;
            [].forEach.call(dropdownParent.children, (opt: HTMLLIElement) => {
                i = i + 1;
                const optRef = connectedSelect.options[i];
                opt.dataset.lastClicked = "false";
                if (i < startIndex || i > endIndex) {
                    if (!opt.classList.contains('aria-selected')) {
                        return;
                    }
                    this.NP.toggleAriaState(opt, "selected", false);
                    optRef.selected = false;
                    return;
                }
                this.NP.toggleAriaState(opt, "selected", true);
                optRef.selected = true;

            });

            // Then trigger the change event on the connected <select> element
            connectedSelect.dispatchEvent(new Event('change'));
        });

        // let selector = 'form.branded select:not(.no-brand), select.branded';
        this.NP.components.formSelect = (selector: string | Element | NodeListOf<Element> | Array<Element>): void => {
            this.NP.getComponentSelector(selector).forEach((formSelect: HTMLSelectElement) => {
                if (formSelect.children.length === 0) {
                    return;
                }
                let extraClasses: Array<string> = null;
                if (formSelect.classList.length > 0) {
                    extraClasses = Array.from(formSelect.classList).filter((c) => { return !["branded", "auto-size"].includes(c); });
                }
                formSelect.classList.add('branded');
    
                const isMulti = formSelect.multiple;
                const isAutoSized = formSelect.classList.contains('auto-size');
    
                // Create a container for the dropdown.
                const dropdownContainer = document.createElement('div');
                dropdownContainer.classList.add(...["dropdown", "form-element"]);
                if (extraClasses !== null) {
                    // Add any additional custom classes that were included in the original <select> element.
                    dropdownContainer.classList.add(...extraClasses);
                }
                dropdownContainer.classList.toggle("auto-size", isAutoSized);
    
                // Create a container for the options and add a data reference to the original <select> element.
                const optionList = document.createElement('ul');
                optionList.setAttribute('role', 'listbox');
                this.NP.toggleAriaState(optionList, "expanded", true);
    
                // Loop through all children of the <select> element.
                [].slice.call(formSelect.children).forEach((cn: HTMLOptionElement) => {
                    // If this element is an <option> create the HTML structure and add it to the optionList.
                    // A reference to the original <option> is also added to the data properties.
                    if (cn.nodeName === 'OPTION') {
                        const thisOption = cn;
                        const newOption = document.createElement('li');
                        newOption.setAttribute('role', 'option');
                        this.NP.toggleAriaState(newOption, "selected", thisOption.selected || false);
                        newOption.innerHTML = thisOption.innerHTML;
                        // For multi-select elements we apply a specific click event here. Otherwise the default
                        // click event is applied in the dropdown.js file.
                        if (isMulti) {
                            newOption.dataset.lastClicked = 'false';
                            newOption.addEventListener("click", (ev: Event) => this.NP.events.selectMultiOptionClick(ev));
                        }
                        optionList.appendChild(newOption);
                        return;
                    }
                    // If this element is an <optgroup> create the HTML structure and add it to the optionList.
                    // Then loop through its children and add the appropriate options similar to above.
                    if (cn.nodeName === 'OPTGROUP') {
                        const thisOptgroup = cn;
                        const newLiGroup = document.createElement('li');
                        newLiGroup.classList.add('group');
                        newLiGroup.innerHTML = thisOptgroup.label;
                        // optionList.appendChild(newLiGroup); // uncomment this if we want to include group text above options
    
                        [].slice.call(thisOptgroup.children).forEach((ocn: HTMLOptionElement) => {
                            const thisOption = ocn;
                            const newOption = document.createElement('li');
                            newOption.classList.add('in-group');
                            newOption.setAttribute('role', 'option');
                            this.NP.toggleAriaState(newOption, "selected", thisOption.selected || false);
                            newOption.innerHTML = thisOption.innerHTML;
                            if (isMulti) {
                                newOption.dataset.lastClicked = 'false';
                                newOption.addEventListener("click", (ev: Event) => this.NP.events.selectMultiOptionClick(ev));
                            }
                            optionList.appendChild(newOption);
                            return;
                        });
                    }
                });
                
                // If this is a multi-select end the function
                if (isMulti) {
                    optionList.classList.add('select-multiple');
                    formSelect.before(optionList);
                    // Determine auto-sizing
                    if (isAutoSized) {
                        optionList.classList.add('auto-size');
                        let widestOption = 0;
                        [].slice.call(optionList.children).forEach((cn: HTMLLIElement) => {
                            const w = cn.clientWidth + (cn.classList.contains("in-group") ? 20 : 0);
                            if (w <= widestOption) {
                                return;
                            }
                            widestOption = w;
                        });
                        optionList.style.width = widestOption + 'px';
                        [].slice.call(optionList.children).forEach((cn: HTMLLIElement) => {
                            cn.style.width = widestOption + 'px';
                        });
                    } else {
                        optionList.style.alignItems = 'stretch';
                    }
                    return;
                }
    
                // If this is not a multi-select find the selected option or set the first option to selected.
                const selectedOption = formSelect.options[formSelect.options.selectedIndex];
                let buttonHtml = '';
                if (selectedOption !== undefined) {
                    buttonHtml = selectedOption.innerHTML;
                } else {
                    const firstOption = formSelect.options[0];
                    firstOption.selected = true;
                    buttonHtml = firstOption.innerHTML;
                }
    
                // Add a dropdown button to toggle the aria-expanded state of the dropdown.
                const dropdownButton = document.createElement('button');
                dropdownButton.type = 'button';
                this.NP.toggleAriaState(dropdownButton, "haspopup", true);
                this.NP.toggleAriaState(dropdownButton, "pressed", false);
                dropdownButton.innerHTML = buttonHtml;
                dropdownContainer.append(dropdownButton, optionList);
    
                formSelect.parentNode.insertBefore(dropdownContainer, formSelect);
                this.NP.components.dropdown([dropdownContainer]);
            });
        }
    };
}